"use client";

import type { TemplateSlot, TemplateType } from "@/features/calendar/types/templates";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../dialog.tsx";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions.tsx";

interface TemplateSlotDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  templateId: string;
  templateType: TemplateType;
  slotId: string | null;
}

export function TemplateSlotDetailsDialog({
  isOpen,
  onClose,
  templateId,
  templateType,
  slotId,
}: TemplateSlotDetailsDialogProps) {
  const { t } = useTranslation(["common", "calendar", "forms"]);
  const [isLoading, setIsLoading] = useState(true);
  const [slotData, setSlotData] = useState<TemplateSlot | null>(null);
  const hostingOptions = useHostingOptions();

  // Fetch slot details when dialog opens
  useEffect(() => {
    if (isOpen && slotId) {
      fetchSlotDetails();
    }
  }, [isOpen, slotId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSlotData(null);
      setIsLoading(true);
    }
  }, [isOpen]);

  const fetchSlotDetails = async () => {
    if (!slotId) return;

    try {
      setIsLoading(true);
      const { data } = await axiosApiInstance.get(
        `${API_ENDPOINTS.TUTOR.TEMPLATES.LIST}/${templateId}/slots/${slotId}`,
      );

      setSlotData(data.data);
    } catch (error) {
      console.error("Failed to fetch slot details:", error);
      toast.error(t("common:error"), {
        description: t("calendar:templates.failedToLoadSlotDetails"),
      });
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  // Get hosting option details
  const getHostingOption = (hostId: string) => {
    return (
      hostingOptions.find((option) => option.id === hostId) || {
        title: t("calendar:templates.unknownHost"),
        description: "",
        icon: <Icon className="text-muted-foreground h-5 w-5" icon="tabler:question-mark" />,
      }
    );
  };

  // Get day name from day number
  const getDayName = (dayNumber: number | null) => {
    if (dayNumber === null) return t("calendar:templates.daily");

    const days = [
      t("calendar:days.monday"),
      t("calendar:days.tuesday"),
      t("calendar:days.wednesday"),
      t("calendar:days.thursday"),
      t("calendar:days.friday"),
      t("calendar:days.saturday"),
      t("calendar:days.sunday"),
    ];

    return days[dayNumber - 1] || t("calendar:templates.unknownDay");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        aria-describedby={undefined}
        className="bg-background absolute! left-0 flex h-dvh max-w-full flex-col gap-0 rounded-e-none! border-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:left-[35%] md:max-h-dvh md:max-w-[65%] xl:left-[60%] xl:max-w-[40vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          animate={{ opacity: 1, x: 0 }}
          className="flex h-full flex-col"
          exit={{ opacity: 0, x: 50 }}
          initial={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <DialogHeader className="border-b px-6 py-4">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: -10 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
                <Icon
                  className="text-accent-foreground"
                  height={"1.25rem"}
                  icon="tabler:info-circle"
                  width={"1.25rem"}
                />
                {t("calendar:templates.slotDetails")}
              </DialogTitle>
            </motion.div>
          </DialogHeader>

          {isLoading ? (
            <div className="flex flex-grow items-center justify-center">
              <motion.div
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-3"
                initial={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <div className="relative h-12 w-12">
                  <motion.div
                    animate={{ rotate: 360 }}
                    className="border-muted-foreground/30 absolute inset-0 rounded-full border-4"
                    transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  />
                  <motion.div
                    animate={{ rotate: 360 }}
                    className="border-t-accent-foreground absolute inset-0 rounded-full border-4 border-r-transparent border-b-transparent border-l-transparent"
                    transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                  />
                </div>
                <p className="text-muted-foreground text-sm">
                  {t("calendar:templates.loadingDetails")}
                </p>
              </motion.div>
            </div>
          ) : (
            <AnimatePresence>
              <ScrollArea className="flex-grow">
                <div className="space-y-6 p-6">
                  {/* Time and Day Section */}
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.1, duration: 0.4 }}
                  >
                    <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-gradient-to-br from-blue-400 to-purple-600" />
                        <div className="absolute -bottom-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-tr from-green-400 to-emerald-500" />
                      </div>

                      <CardContent className="relative p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-accent-foreground flex items-center gap-2 text-lg font-semibold">
                              <Icon height={"1.25rem"} icon="tabler:clock" width={"1.25rem"} />
                              {t("calendar:templates.timeSlot")}
                            </h3>
                            <motion.p
                              animate={{ opacity: 1, scale: 1 }}
                              className="mt-3 text-3xl font-bold"
                              initial={{ opacity: 0, scale: 0.9 }}
                              transition={{ delay: 0.2, duration: 0.3 }}
                            >
                              {slotData?.start_time.slice(0, 5)} - {slotData?.end_time.slice(0, 5)}
                            </motion.p>
                          </div>
                        </div>

                        {templateType === "weekly" && slotData?.day_of_week && (
                          <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-muted/20 mt-6 rounded-xl p-4 backdrop-blur-sm"
                            initial={{ opacity: 0, y: 10 }}
                            transition={{ delay: 0.3, duration: 0.3 }}
                          >
                            <h3 className="text-accent-foreground flex items-center gap-2 text-base font-semibold">
                              <Icon height={"1.25rem"} icon="tabler:calendar" width={"1.25rem"} />
                              {t("calendar:templates.dayOfWeek")}
                            </h3>
                            <p className="text-muted-foreground mt-2 text-lg font-medium">
                              {getDayName(slotData.day_of_week)}
                            </p>
                          </motion.div>
                        )}

                        {slotData?.topic && (
                          <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-muted/20 mt-6 rounded-xl p-4 backdrop-blur-sm"
                            initial={{ opacity: 0, y: 10 }}
                            transition={{ delay: 0.4, duration: 0.3 }}
                          >
                            <h3 className="text-accent-foreground flex items-center gap-2 text-base font-semibold">
                              <Icon height={"1.25rem"} icon="tabler:book" width={"1.25rem"} />
                              {t("calendar:templates.topic")}
                            </h3>
                            <p className="text-muted-foreground mt-2 text-lg font-medium">
                              {slotData.topic}
                            </p>
                          </motion.div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Hosting Method Section */}
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                  >
                    <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute -top-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-br from-violet-400 to-purple-600" />
                        <div className="absolute -right-16 -bottom-16 h-32 w-32 rounded-full bg-gradient-to-tr from-orange-400 to-red-500" />
                      </div>

                      <CardContent className="relative p-6">
                        <h3 className="text-accent-foreground flex items-center gap-2 text-lg font-semibold">
                          <Icon height={"1.25rem"} icon="tabler:device-laptop" width={"1.25rem"} />
                          {t("calendar:templates.lessonHost")}
                        </h3>

                        {slotData?.lesson_host && (
                          <motion.div
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-muted/20 hover:bg-muted/30 mt-4 flex items-center gap-4 rounded-xl p-4 backdrop-blur-sm transition-all"
                            initial={{ opacity: 0, y: 10 }}
                            transition={{ delay: 0.3, duration: 0.3 }}
                            whileHover={{ y: -2, boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}
                          >
                            <div className="bg-muted/30 flex size-12 items-center justify-center rounded-xl backdrop-blur-sm">
                              {getHostingOption(slotData.lesson_host).icon}
                            </div>
                            <div>
                              <span className="text-accent-foreground text-lg font-semibold">
                                {getHostingOption(slotData.lesson_host).title}
                              </span>
                              <p className="text-muted-foreground mt-1 text-sm">
                                {getHostingOption(slotData.lesson_host).description}
                              </p>
                            </div>
                          </motion.div>
                        )}

                        {slotData?.lesson_host === "other_way" &&
                          slotData?.communication_details && (
                            <motion.div
                              animate={{ opacity: 1, y: 0 }}
                              className="mt-6"
                              initial={{ opacity: 0, y: 10 }}
                              transition={{ delay: 0.4, duration: 0.3 }}
                            >
                              <h3 className="text-accent-foreground flex items-center gap-2 text-base font-semibold">
                                <Icon
                                  height={"1.25rem"}
                                  icon="tabler:message-circle"
                                  width={"1.25rem"}
                                />
                                {t("forms:hostMethodSelector.communicationDetails")}
                              </h3>
                              <p className="text-muted-foreground bg-muted/20 mt-3 rounded-xl p-4 backdrop-blur-sm">
                                {slotData.communication_details}
                              </p>
                            </motion.div>
                          )}
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Invitation Message */}
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3, duration: 0.4 }}
                  >
                    <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute -top-12 -right-12 h-24 w-24 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600" />
                        <div className="absolute -bottom-20 -left-20 h-40 w-40 rounded-full bg-gradient-to-tr from-pink-400 to-rose-500" />
                      </div>

                      <CardContent className="relative p-6">
                        <h3 className="text-accent-foreground flex items-center gap-2 text-lg font-semibold">
                          <Icon height={"1.25rem"} icon="tabler:mail" width={"1.25rem"} />
                          {t("calendar:templates.invitationMessage")}
                        </h3>
                        <motion.p
                          animate={{ opacity: 1, y: 0 }}
                          className="text-muted-foreground bg-muted/20 mt-4 rounded-xl p-4 whitespace-pre-wrap backdrop-blur-sm"
                          initial={{ opacity: 0, y: 10 }}
                          transition={{ delay: 0.4, duration: 0.3 }}
                        >
                          {slotData?.invitation_message ||
                            t("calendar:templates.noInvitationMessage")}
                        </motion.p>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Metadata Section */}
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.4, duration: 0.4 }}
                  >
                    <Card className="bg-foreground/50 relative overflow-hidden rounded-2xl border-none shadow-2xl backdrop-blur-xl">
                      {/* Background Pattern */}
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute -top-16 -left-16 h-32 w-32 rounded-full bg-gradient-to-br from-emerald-400 to-teal-600" />
                        <div className="absolute -right-12 -bottom-12 h-24 w-24 rounded-full bg-gradient-to-tr from-yellow-400 to-orange-500" />
                      </div>

                      <CardContent className="relative p-6">
                        <h3 className="text-accent-foreground flex items-center gap-2 text-lg font-semibold">
                          <Icon height={"1.25rem"} icon="tabler:info-square" width={"1.25rem"} />
                          {t("calendar:templates.metadata")}
                        </h3>

                        <motion.div
                          animate={{ opacity: 1 }}
                          className="mt-4 space-y-4"
                          initial={{ opacity: 0 }}
                          transition={{ delay: 0.5, duration: 0.3 }}
                        >
                          <div className="bg-muted/20 hover:bg-muted/30 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm transition-colors">
                            <span className="text-muted-foreground flex items-center gap-2 text-base font-medium">
                              <Icon height={"1.25rem"} icon="tabler:users" width={"1.25rem"} />
                              {t("calendar:templates.lessonType")}
                            </span>
                            <Badge className="rounded-full font-medium" variant="outline">
                              {slotData?.lesson_type === "individual"
                                ? t("calendar:templates.individual")
                                : t("calendar:templates.group")}
                            </Badge>
                          </div>

                          <Separator className="bg-muted-foreground/10" />

                          <div className="bg-muted/20 hover:bg-muted/30 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm transition-colors">
                            <span className="text-muted-foreground flex items-center gap-2 text-base font-medium">
                              <Icon
                                height={"1.25rem"}
                                icon="tabler:calendar-plus"
                                width={"1.25rem"}
                              />
                              {t("calendar:templates.created")}
                            </span>
                            <span className="text-accent-foreground font-semibold">
                              {slotData?.created_at
                                ? new Date(slotData.created_at).toLocaleDateString()
                                : t("calendar:templates.unknown")}
                            </span>
                          </div>

                          <Separator className="bg-muted-foreground/10" />

                          <div className="bg-muted/20 hover:bg-muted/30 flex items-center justify-between rounded-xl p-4 backdrop-blur-sm transition-colors">
                            <span className="text-muted-foreground flex items-center gap-2 text-base font-medium">
                              <Icon height={"1.25rem"} icon="tabler:refresh" width={"1.25rem"} />
                              {t("calendar:templates.lastUpdated")}
                            </span>
                            <span className="text-accent-foreground font-semibold">
                              {slotData?.updated_at
                                ? new Date(slotData.updated_at).toLocaleDateString()
                                : t("calendar:templates.unknown")}
                            </span>
                          </div>
                        </motion.div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              </ScrollArea>

              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="bg-foreground flex h-16 items-center justify-end border-t px-6"
                initial={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <motion.div
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <Button className="gap-2" size="lg" onClick={onClose}>
                    <Icon height={"1rem"} icon="tabler:x" width={"1rem"} />
                    {t("common:close")}
                  </Button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          )}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
